/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        User Workspace Dashboard Overview View (Task 3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo } from 'react';
import {
  Sparkles,
  Image as ImageIcon,
  Languages,
  Wand2,
  MessageSquare,
  ArrowRight,
  TrendingUp,
  ChevronRight,
} from 'lucide-react';
import { SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';
import { useWalletStore } from '../stores/useWalletStore';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useCreditLedgerStore } from '../stores/useCreditLedgerStore';
import { useEditorStore } from '../stores/useEditorStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useSubscriptionStore } from '../stores/useSubscriptionStore';
import { useI18n } from '../hooks/useI18n';

/**
 * Maps document category to standardized high-tech Sophia vector icons.
 */
function getDocumentCategoryIcon(category: string): SophiaDefaultIconName {
  switch (category.toLowerCase()) {
    case 'strategy':
      return 'strategy';
    case 'engineering':
      return 'binary-code';
    case 'marketing':
      return 'brainstorm';
    case 'product':
      return 'solution';
    case 'voice':
    case 'audio':
      return 'voice-recognition';
    case 'image':
    case 'media':
      return 'vision';
    case 'cat':
    case 'translation':
      return 'nano-tech';
    default:
      return 'algorithm';
  }
}

export interface DashboardOverviewDocument {
  id: string;
  title: string;
  category: string;
  words: number;
  updatedAt: string;
  sampleHtml: string;
}

export const DEMO_RECENT_DOCUMENTS: DashboardOverviewDocument[] = [
  {
    id: 'doc_1',
    title: 'Q4 Global AI Strategy Blueprint',
    category: 'Strategy',
    words: 1420,
    updatedAt: '15m ago',
    sampleHtml: '<h2>Q4 Global AI Strategy Blueprint</h2><p>Enterprise Edge AI deployment plan across APAC and EMEA regions.</p><p>Key focus areas include deterministic prompt pipelines, sub-15ms edge inference, and multi-tenant security.</p>',
  },
  {
    id: 'doc_2',
    title: 'Edge Workers Serverless Architecture',
    category: 'Engineering',
    words: 2850,
    updatedAt: 'Yesterday',
    sampleHtml: '<h2>Edge Workers Serverless Architecture</h2><p>V8 Isolate micro-benchmarks with sub-millisecond cold starts and distributed session KV caching.</p>',
  },
  {
    id: 'doc_3',
    title: 'High-Converting SaaS Landing Page Copy',
    category: 'Marketing',
    words: 890,
    updatedAt: '2 days ago',
    sampleHtml: '<h2>High-Converting SaaS Landing Page Copy</h2><p>PAS framework value proposition, social proof citations, and hero section headlines.</p>',
  },
  {
    id: 'doc_4',
    title: 'Product Hunt Launch Announcement',
    category: 'Product',
    words: 620,
    updatedAt: '4 days ago',
    sampleHtml: '<h2>Product Hunt Launch Announcement</h2><p>Community post outline and maker commentary for v2.0 edge-native release.</p>',
  },
];

interface DashboardOverviewViewProps {
  onNavigate: (path: string) => void;
  initialDocuments?: DashboardOverviewDocument[];
  initialUser?: any;
  initialEntries?: any[];
}

export const DashboardOverviewView: React.FC<DashboardOverviewViewProps> = ({
  onNavigate,
  initialDocuments,
  initialUser,
  initialEntries,
}) => {
  const { t } = useI18n();
  const walletStore = useWalletStore();
  const workspaceStore = useWorkspaceStore();
  const ledgerStore = useCreditLedgerStore();
  const authStore = useAuthStore();
  const subStore = useSubscriptionStore();
  const { setTitle, setContent, resetEditor } = useEditorStore();

  // In SSR / renderToStaticMarkup, Zustand useSyncExternalStore may return getInitialState.
  // Fall back to getState() if the active state has been set via setState.
  const balance = walletStore.balance || useWalletStore.getState().balance;
  const monthlyQuota = walletStore.monthlyQuota || useWalletStore.getState().monthlyQuota;
  const activeWorkspaceId = workspaceStore.activeWorkspaceId || useWorkspaceStore.getState().activeWorkspaceId;
  const workspaces = workspaceStore.workspaces.length > 0 ? workspaceStore.workspaces : useWorkspaceStore.getState().workspaces;
  const entries = initialEntries ?? (ledgerStore.entries.length > 0 ? ledgerStore.entries : useCreditLedgerStore.getState().entries);
  const user = initialUser !== undefined ? initialUser : (authStore.user || useAuthStore.getState().user);
  const subscription = subStore.subscription || useSubscriptionStore.getState().subscription;

  const currentWorkspace = useMemo(() => {
    return workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0];
  }, [workspaces, activeWorkspaceId]);

  // Credit quota percent calculation
  const quotaPercent = useMemo(() => {
    if (!monthlyQuota || monthlyQuota <= 0) return 100;
    const pct = Math.round((balance / monthlyQuota) * 100);
    return Math.min(100, Math.max(0, pct));
  }, [balance, monthlyQuota]);

  const usedQuotaPercent = Math.max(0, 100 - quotaPercent);
  const usedCredits = Math.max(0, monthlyQuota - balance);

  // Circular gauge circumference for SVG (r = 38)
  const circleRadius = 38;
  const circleCircumference = 2 * Math.PI * circleRadius; // ~238.76
  const strokeDashoffset = circleCircumference - (quotaPercent / 100) * circleCircumference;

  // Display user name and fallback initial
  const userName = user?.name || user?.fullName || 'Creator';
  const userInitial = (userName.charAt(0) || 'C').toUpperCase();
  const userEmail = user?.email || '';
  const planTierName = (subscription?.tier || 'free').toUpperCase() + ' TIER';

  // Check if current workspace or environment has demo data active
  const isDemoActive = useMemo(() => {
    if (initialDocuments !== undefined) return true;
    return Boolean(
      (activeWorkspaceId && activeWorkspaceId.includes('demo')) ||
      workspaces.some((w) => w.id?.includes('demo'))
    );
  }, [initialDocuments, activeWorkspaceId, workspaces]);

  // Documents list: use initialDocuments or DEMO_RECENT_DOCUMENTS when in demo mode, else clean empty array
  const recentDocuments = useMemo(() => {
    if (initialDocuments !== undefined) return initialDocuments;
    if (isDemoActive) return DEMO_RECENT_DOCUMENTS;
    return [];
  }, [initialDocuments, isDemoActive]);

  // Top 5 recent activities from credit ledger
  const recentActivities = useMemo(() => {
    return entries.slice(0, 5);
  }, [entries]);

  // Open existing document directly into TipTap editor
  const handleOpenDocument = (doc: (typeof recentDocuments)[0]) => {
    setTitle(doc.title);
    setContent(doc.sampleHtml);
    onNavigate('/workspace/documents');
  };

  // Create new document and navigate to TipTap editor
  const handleCreateNewDocument = () => {
    resetEditor();
    setTitle('Untitled Document');
    setContent('<p></p>');
    onNavigate('/workspace/documents');
  };

  // 5 Main Creative Studios Configuration with Standardized Sophia Icons
  const mainStudios: Array<{
    id: string;
    title: string;
    desc: string;
    icon: typeof Sparkles;
    sophiaIcon: SophiaDefaultIconName;
    color: string;
    hoverBorder: string;
    path: string;
  }> = [
    {
      id: 'writer',
      title: t('dashboard.quickStudios.aiWriter'),
      desc: t('dashboard.quickStudios.aiWriterDesc'),
      icon: Sparkles,
      sophiaIcon: 'artificial-intelligence',
      color: 'text-primary bg-primary/10 border-primary/20',
      hoverBorder: 'hover:border-primary/50 hover:shadow-glow-primary/20',
      path: '/workspace/writer',
    },
    {
      id: 'wizard',
      title: t('dashboard.quickStudios.wizard'),
      desc: t('dashboard.quickStudios.wizardDesc'),
      icon: Wand2,
      sophiaIcon: 'algorithm',
      color: 'text-accent bg-accent/10 border-accent/20',
      hoverBorder: 'hover:border-accent/50 hover:shadow-glow-accent/20',
      path: '/workspace/wizard',
    },
    {
      id: 'chat',
      title: t('dashboard.quickStudios.chatStudio'),
      desc: t('dashboard.quickStudios.chatStudioDesc'),
      icon: MessageSquare,
      sophiaIcon: 'chat-bot',
      color: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20',
      hoverBorder: 'hover:border-cyan-500/50 hover:shadow-cyan-500/20',
      path: '/workspace/chat',
    },
    {
      id: 'image',
      title: t('dashboard.quickStudios.imageStudio'),
      desc: t('dashboard.quickStudios.imageStudioDesc'),
      icon: ImageIcon,
      sophiaIcon: 'vision',
      color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
      hoverBorder: 'hover:border-emerald-500/50 hover:shadow-emerald-500/20',
      path: '/workspace/media/images',
    },
    {
      id: 'cat',
      title: t('dashboard.quickStudios.catStudio'),
      desc: t('dashboard.quickStudios.catStudioDesc'),
      icon: Languages,
      sophiaIcon: 'nano-tech',
      color: 'text-primary bg-primary/10 border-primary/20',
      hoverBorder: 'hover:border-primary/50 hover:shadow-glow-primary/20',
      path: '/workspace/translation',
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Welcome Header & Account Status Block */}
      <div className="relative overflow-hidden rounded-3xl border border-surface-border bg-surface p-6 sm:p-8 shadow-glass">
        {/* Ambient Glow Accents */}
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          {/* User Profile, Avatar & Workspace Status */}
          <div className="flex items-start sm:items-center gap-4 sm:gap-5">
            {/* User Avatar with Gradient Ring & Online Dot */}
            <div className="relative shrink-0">
              <div className="size-14 sm:size-16 rounded-2xl bg-gradient-to-tr from-primary to-accent p-0.5 shadow-glow-primary/20">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={userName}
                    className="size-full rounded-[14px] object-cover"
                  />
                ) : (
                  <div className="size-full rounded-[14px] bg-surface flex items-center justify-center font-heading font-extrabold text-xl sm:text-2xl text-primary uppercase">
                    {userInitial}
                  </div>
                )}
              </div>
              <span
                className="absolute -bottom-1 -right-1 size-4 rounded-full bg-emerald-500 border-2 border-surface"
                title="Account Active"
              />
            </div>

            {/* Greetings & Metadata */}
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  <SophiaIcon name="structure-server" size={12} className="text-primary shrink-0" />
                  <span>{currentWorkspace?.name || 'Sophia Creative Labs'}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-accent/15 text-accent border border-accent/25">
                  <SophiaIcon name="hologram" size={11} className="text-accent shrink-0" />
                  <span>{planTierName}</span>
                </span>
              </div>

              <h1 className="text-xl sm:text-2xl lg:text-3xl font-heading font-extrabold text-foreground tracking-tight">
                {t('dashboard.welcomeBack')}, {userName}!
              </h1>

              <p className="text-foreground-muted text-xs sm:text-sm">
                {userEmail} &bull; {t('dashboard.subtitle')}
              </p>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto self-stretch md:self-auto">
            <button
              type="button"
              onClick={() => onNavigate('/workspace/billing/plans')}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs font-semibold bg-gradient-to-r from-primary to-accent text-white shadow-glow-primary hover:opacity-95 transition-all cursor-pointer"
            >
              <SophiaIcon name="wireless-charger" size={14} className="text-white shrink-0" />
              <span>{t('dashboard.upgradePlan')}</span>
            </button>

            <button
              type="button"
              onClick={() => onNavigate('/workspace/settings')}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-semibold text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
            >
              <SophiaIcon name="finger-scanner" size={14} className="text-foreground-muted shrink-0" />
              <span>{t('dashboard.profile')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Key Metrics & Circular Credit Gauge Widget */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Widget 1: Circular Credit Quota Gauge (5 columns on desktop) */}
        <div className="md:col-span-12 lg:col-span-5 p-6 rounded-3xl border border-surface-border bg-surface-elevated/70 backdrop-blur-md shadow-glass flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between border-b border-surface-border/60 pb-3">
            <div className="flex items-center gap-2">
              <SophiaIcon name="battery" size={16} className="text-amber-500 shrink-0" />
              <h3 className="text-xs font-bold font-heading uppercase tracking-wider text-foreground">
                {t('dashboard.kpi.creditsTitle')}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => onNavigate('/workspace/billing/plans')}
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline cursor-pointer"
            >
              <span>{t('dashboard.quickTopup')}</span>
              <ChevronRight className="size-3" />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6 py-2">
            {/* SVG Circular Progress Ring */}
            <div className="relative shrink-0 flex items-center justify-center">
              <svg className="size-28 -rotate-90 transform" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="credit-gauge-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(var(--color-primary))" />
                    <stop offset="100%" stopColor="hsl(var(--color-accent))" />
                  </linearGradient>
                </defs>
                {/* Track circle */}
                <circle
                  cx="50"
                  cy="50"
                  r={circleRadius}
                  stroke="currentColor"
                  strokeWidth="8"
                  className="text-surface-border"
                  fill="transparent"
                />
                {/* Progress circle */}
                <circle
                  cx="50"
                  cy="50"
                  r={circleRadius}
                  stroke="url(#credit-gauge-grad)"
                  strokeWidth="8"
                  strokeDasharray={circleCircumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  className="transition-all duration-700 ease-out"
                  fill="transparent"
                />
              </svg>

              {/* Inside Gauge Center Display with battery icon */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <SophiaIcon name="battery" size={16} className="text-amber-500 mb-0.5" />
                <span className="text-xl font-heading font-black text-foreground tracking-tight">
                  {quotaPercent}%
                </span>
                <span className="text-[10px] text-foreground-muted font-medium">
                  Available
                </span>
              </div>
            </div>

            {/* Quota Numbers & Fast Recharge CTA */}
            <div className="flex-1 space-y-2 text-center sm:text-left">
              <div>
                <span className="text-2xl font-heading font-black text-foreground tracking-tight">
                  {balance.toLocaleString()}
                </span>
                <span className="text-xs text-foreground-muted font-mono ml-1.5">
                  / {monthlyQuota.toLocaleString()}
                </span>
              </div>

              <p className="text-xs text-foreground-muted">
                {t('dashboard.usedQuota')}: <span className="font-semibold text-foreground">{usedCredits.toLocaleString()}</span> ({usedQuotaPercent}%)
              </p>

              <button
                type="button"
                onClick={() => onNavigate('/workspace/billing/plans')}
                className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary/10 border border-primary/20 text-xs font-semibold text-primary hover:bg-primary/20 transition-all cursor-pointer"
              >
                <SophiaIcon name="wireless-charger" size={13} className="text-primary shrink-0" />
                <span>{t('dashboard.quickTopup')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* 3 Secondary KPI Metrics (7 columns on desktop) */}
        <div className="md:col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Card 2: Documents Created */}
          <div className="p-5 rounded-3xl border border-surface-border bg-surface-elevated/70 backdrop-blur-md shadow-glass flex flex-col justify-between space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground-muted">
                {t('dashboard.kpi.documentsTitle')}
              </span>
              <div className="size-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20">
                <SophiaIcon name="brainstorm" size={18} className="text-primary shrink-0" />
              </div>
            </div>

            <div>
              <span className="text-2xl font-heading font-black text-foreground tracking-tight">
                142
              </span>
              <div className="flex items-center gap-1 text-[11px] text-emerald-500 font-medium mt-1">
                <TrendingUp className="size-3" />
                <span>{t('dashboard.kpi.documentsSub')}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onNavigate('/workspace/documents')}
              className="text-[11px] text-primary font-semibold hover:underline flex items-center gap-0.5 self-start pt-1 cursor-pointer"
            >
              <span>{t('dashboard.recentDocs.viewAll')}</span>
              <ChevronRight className="size-3" />
            </button>
          </div>

          {/* Card 3: Media Creations */}
          <div className="p-5 rounded-3xl border border-surface-border bg-surface-elevated/70 backdrop-blur-md shadow-glass flex flex-col justify-between space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground-muted">
                {t('dashboard.kpi.mediaTitle')}
              </span>
              <div className="size-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center border border-accent/20">
                <SophiaIcon name="vision" size={18} className="text-accent shrink-0" />
              </div>
            </div>

            <div>
              <span className="text-2xl font-heading font-black text-foreground tracking-tight">
                85
              </span>
              <div className="text-[11px] text-foreground-muted font-mono mt-1">
                {t('dashboard.kpi.mediaSub')}
              </div>
            </div>

            <button
              type="button"
              onClick={() => onNavigate('/workspace/gallery')}
              className="text-[11px] text-accent font-semibold hover:underline flex items-center gap-0.5 self-start pt-1 cursor-pointer"
            >
              <span>{t('dashboard.recentDocs.viewAll')}</span>
              <ChevronRight className="size-3" />
            </button>
          </div>

          {/* Card 4: CAT Translated Words */}
          <div className="p-5 rounded-3xl border border-surface-border bg-surface-elevated/70 backdrop-blur-md shadow-glass flex flex-col justify-between space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground-muted">
                {t('dashboard.kpi.catWordsTitle')}
              </span>
              <div className="size-9 rounded-xl bg-cyan-500/10 text-cyan-500 flex items-center justify-center border border-cyan-500/20">
                <SophiaIcon name="nano-tech" size={18} className="text-cyan-500 shrink-0" />
              </div>
            </div>

            <div>
              <span className="text-2xl font-heading font-black text-foreground tracking-tight">
                42,600
              </span>
              <div className="flex items-center gap-1 text-[11px] text-emerald-500 font-medium mt-1">
                <Languages className="size-3" />
                <span>{t('dashboard.kpi.catWordsSub')}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onNavigate('/workspace/translation')}
              className="text-[11px] text-cyan-500 font-semibold hover:underline flex items-center gap-0.5 self-start pt-1 cursor-pointer"
            >
              <span>{t('dashboard.recentDocs.viewAll')}</span>
              <ChevronRight className="size-3" />
            </button>
          </div>
        </div>
      </div>

      {/* 3. 5-Studio Creative Quick Launcher Grid */}
      <div className="p-6 sm:p-7 rounded-3xl border border-surface-border bg-surface shadow-glass space-y-5">
        <div className="flex items-center justify-between border-b border-surface-border/60 pb-3">
          <div>
            <h3 className="text-base font-heading font-bold text-foreground">
              {t('dashboard.quickStudios.title')}
            </h3>
            <p className="text-xs text-foreground-muted">
              {t('dashboard.quickStudios.subtitle')}
            </p>
          </div>
        </div>

        {/* 5 Studio Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5 sm:gap-4">
          {mainStudios.map((studio) => {
            return (
              <div
                key={studio.id}
                onClick={() => onNavigate(studio.path)}
                className={`group p-4 sm:p-5 rounded-2xl border border-surface-border bg-surface-elevated/70 backdrop-blur-md ${studio.hoverBorder} hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col justify-between space-y-4`}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={`size-10 rounded-xl flex items-center justify-center border ${studio.color} group-hover:scale-110 transition-transform`}
                  >
                    <SophiaIcon name={studio.sophiaIcon} size={20} className="size-5 shrink-0" />
                  </div>
                  <ArrowRight className="size-4 text-foreground-muted group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </div>

                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-foreground group-hover:text-primary transition-colors">
                    {studio.title}
                  </h4>
                  <p className="text-[11px] text-foreground-muted line-clamp-2 mt-1 leading-snug">
                    {studio.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 4. Dual Columns: Recent Documents (Left) & Activity Feed (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Left Column: Recent Documents (7 cols) with direct TipTap Editor Link */}
        <div className="lg:col-span-7 p-6 sm:p-7 rounded-3xl border border-surface-border bg-surface shadow-glass space-y-4">
          <div className="flex items-center justify-between border-b border-surface-border/60 pb-3">
            <div className="flex items-center gap-2">
              <SophiaIcon name="brainstorm" size={16} className="text-primary shrink-0" />
              <h3 className="text-sm sm:text-base font-heading font-bold text-foreground">
                {t('dashboard.recentDocs.title')}
              </h3>
            </div>

            <button
              type="button"
              onClick={handleCreateNewDocument}
              className="flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline cursor-pointer"
            >
              <SophiaIcon name="algorithm" size={13} className="text-primary shrink-0" />
              <span>{t('dashboard.recentDocs.createNew')}</span>
            </button>
          </div>

          <div className="space-y-2.5">
            {recentDocuments.length === 0 ? (
              <div className="py-8 px-4 text-center rounded-2xl border border-dashed border-surface-border bg-surface-elevated/40 flex flex-col items-center justify-center space-y-3">
                <div className="size-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <SophiaIcon name="brainstorm" size={20} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-foreground">
                    {t('dashboard.recentDocs.emptyTitle')}
                  </h4>
                  <p className="text-[11px] text-foreground-muted max-w-xs">
                    {t('dashboard.recentDocs.emptyDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCreateNewDocument}
                  className="px-3.5 py-1.5 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:opacity-90 transition-all flex items-center gap-1.5 shadow-xs"
                >
                  <SophiaIcon name="algorithm" size={13} />
                  <span>{t('dashboard.recentDocs.createNew')}</span>
                </button>
              </div>
            ) : (
              recentDocuments.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => handleOpenDocument(doc)}
                  className="group flex items-center justify-between p-3.5 rounded-2xl border border-surface-border bg-surface-elevated/80 hover:border-primary/50 hover:bg-muted/40 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="size-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                      <SophiaIcon name={getDocumentCategoryIcon(doc.category)} size={18} className="text-primary shrink-0" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                        {doc.title}
                      </h4>
                      <div className="flex items-center gap-2 text-[10px] text-foreground-muted font-mono mt-0.5">
                        <span className="px-2 py-0.5 rounded-md bg-muted text-foreground-muted font-medium">
                          {doc.category}
                        </span>
                        <span>&bull;</span>
                        <span>{t('dashboard.recentDocs.words', { count: doc.words })}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-foreground-muted">
                    <span className="hidden sm:inline font-mono">
                      {t('dashboard.recentDocs.updated', { time: doc.updatedAt })}
                    </span>
                    <ChevronRight className="size-4 text-foreground-muted group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Column: Recent Activity Feed (5 cols) */}
        <div className="lg:col-span-5 p-6 sm:p-7 rounded-3xl border border-surface-border bg-surface shadow-glass space-y-4">
          <div className="flex items-center justify-between border-b border-surface-border/60 pb-3">
            <div className="flex items-center gap-2">
              <SophiaIcon name="big-data" size={16} className="text-accent shrink-0" />
              <h3 className="text-sm sm:text-base font-heading font-bold text-foreground">
                {t('dashboard.activity.title')}
              </h3>
            </div>

            <button
              type="button"
              onClick={() => onNavigate('/workspace/billing/ledger')}
              className="text-xs text-accent font-semibold hover:underline cursor-pointer"
            >
              {t('dashboard.activity.viewLedger')}
            </button>
          </div>

          <div className="space-y-3">
            {recentActivities.length === 0 ? (
              <div className="py-8 px-4 text-center rounded-2xl border border-dashed border-surface-border bg-surface-elevated/40 flex flex-col items-center justify-center space-y-3">
                <div className="size-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
                  <SophiaIcon name="battery" size={20} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-foreground">
                    {t('dashboard.activity.emptyTitle')}
                  </h4>
                  <p className="text-[11px] text-foreground-muted max-w-xs">
                    {t('dashboard.activity.emptyDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate('/workspace/writer')}
                  className="px-3.5 py-1.5 rounded-xl bg-surface-elevated border border-surface-border text-foreground hover:bg-muted text-xs font-semibold transition-all flex items-center gap-1.5 shadow-xs"
                >
                  <SophiaIcon name="artificial-intelligence" size={13} />
                  <span>{t('dashboard.quickStudios.aiWriter')}</span>
                </button>
              </div>
            ) : (
              recentActivities.map((act) => {
                const isCredit = act.amount > 0;
                return (
                  <div
                    key={act.id}
                    className="flex items-start justify-between gap-3 p-3 rounded-2xl border border-surface-border bg-surface-elevated/60 text-xs"
                  >
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div
                        className={`size-7 rounded-xl flex items-center justify-center shrink-0 mt-0.5 border ${
                          isCredit
                            ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                            : 'bg-primary/10 text-primary border-primary/20'
                        }`}
                      >
                        {isCredit ? (
                          <SophiaIcon name="battery" size={14} className="text-emerald-500 shrink-0" />
                        ) : (
                          <SophiaIcon name="cpu" size={14} className="text-primary shrink-0" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground text-[11px] truncate">
                          {act.description}
                        </p>
                        <span className="text-[10px] text-foreground-muted font-mono">
                          {act.createdAt}
                        </span>
                      </div>
                    </div>

                    <span
                      className={`font-mono font-bold text-xs shrink-0 ${
                        isCredit ? 'text-emerald-500' : 'text-rose-500'
                      }`}
                    >
                      {isCredit ? `+${act.amount}` : act.amount}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
